import { DateField } from "@louise/moon-ui";
import { useState } from "react";
import { Text } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenTitle,
  ScreenSubtitle,
  ContinueButton,
} from "../components/OnboardingLayout";
import { useTranslations } from "@src/packages/i18n";
import type { OnboardingStackParamList } from "../types";
import { semanticColors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<OnboardingStackParamList, "Birthday">;

/** Calculate age from date of birth */
function calculateAge(dob: Date): number {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

export default function BirthdayScreen({ navigation }: Props) {
  const t = useTranslations();
  const { data, updateField } = useOnboarding();

  const [dateOfBirth, setDateOfBirth] = useState(data.dateOfBirth ?? "");
  const [completeDate, setCompleteDate] = useState(Boolean(data.dateOfBirth));
  const parsed = dateOfBirth ? new Date(`${dateOfBirth}T00:00:00`) : null;
  const age =
    parsed && !Number.isNaN(parsed.getTime()) ? calculateAge(parsed) : null;
  const validAge = completeDate && age !== null && age >= 18 && age <= 65;

  const handleContinue = () => {
    if (!parsed || !validAge || age === null) return;
    updateField("dateOfBirth", dateOfBirth);
    updateField("age", age);
    navigation.navigate("Location");
  };

  return (
    <OnboardingLayout>
      <ScreenTitle>{t("onboarding.birthday.how_old_are_you")}</ScreenTitle>
      <ScreenSubtitle>
        {t("onboarding.birthday.age_shapes_your_care")}
      </ScreenSubtitle>
      <DateField
        appearance="glass"
        value={dateOfBirth}
        onChange={setDateOfBirth}
        accessibilityLabel={t("onboarding.birthday.enter_birth_date")}
        autoFocus
        onValidityChange={setCompleteDate}
      />
      {validAge && age !== null && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: semanticColors.onDark.text.secondary,
            textAlign: "center",
            marginTop: 10,
          }}
        >
          {t("onboarding.birthday.years_old", { params: { age: String(age) } })}
        </Text>
      )}
      <ContinueButton
        title={t("onboarding.next")}
        onPress={handleContinue}
        disabled={!validAge}
      />
    </OnboardingLayout>
  );
}
